import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDashboardPageState } from '../../../../hooks/useDashboardPageState';
import { useAddUserBlock } from '../../../../api/healthCoach/mutations/useAddUserBlock';
import {
  ADJUSTABLE_NUTRITION_BLOCKS,
  BLOCK_TYPE,
  BlockType,
} from '../../../../models/block';
import type { DashboardBlock, UserBlock } from '../../../../models/block';
import { isAssignableBlock } from '../utils';
import { useUserBlockAction } from '../../../../api/healthCoach/mutations/useUserBlockAction';
import { FilterOption, UserBlockAction } from '../types';
import useUpdateUserBlock from '../../../../api/healthCoach/mutations/useUpdateUserBlock';
import { archivedFilterOptions, laneLabelByType } from '../constants';

export const useOverviewPage = () => {
  const { patient = '' } = useParams();
  const {
    state: { user },
  } = useDashboardPageState();
  const [archivedFilter, setArchivedFilter] = useState<FilterOption>(
    archivedFilterOptions[0]
  );

  const { mutation: addUserBlock } = useAddUserBlock();
  const { mutation: userBlockAction } = useUserBlockAction();
  const { mutation: updateUserBlockMutation } = useUpdateUserBlock();

  const [selectedLaneBlock, setSelectedLaneBlock] = useState<UserBlock | null>(
    null
  );
  const [selectedCreateBlock, setSelectedCreateBlock] =
    useState<DashboardBlock | null>(null);
  const [patientInstructions, setPatientInstructions] = useState('');

  const resetCreateBlockMutationState = () => {
    addUserBlock.reset();
  };

  // if lane block is selected, editMode
  const isEditMode = !!selectedLaneBlock;
  // if dashboard block is selected, createMode
  const isCreateMode = !isEditMode && !!selectedCreateBlock;
  // if creating or editing, drawer is open
  const isDrawerOpen = isCreateMode || isEditMode;

  const onCreateBlock = async (block: DashboardBlock) => {
    if (!patient || !block.id || !isAssignableBlock(block)) return;

    resetCreateBlockMutationState();

    await addUserBlock.mutateAsync({
      blockId: block.id,
      instructions: patientInstructions,
      userId: patient,
    });

    setSelectedCreateBlock(null);
    setSelectedLaneBlock(null);
    setPatientInstructions('');
  };

  const handleUserBlockAction = async (
    action: UserBlockAction,
    userBlockId?: string
  ) => {
    if (userBlockId) {
      await userBlockAction.mutateAsync({
        userId: patient,
        userBlockId,
        action,
      });
    }

    closeBlockDrawer();
  };

  const closeBlockDrawer = () => {
    resetCreateBlockMutationState();
    setSelectedCreateBlock(null);
    setSelectedLaneBlock(null);
    setPatientInstructions('');
  };

  const handleUpdateUserBlock = async () => {
    const userBlockId = selectedLaneBlock?.id;

    if (userBlockId) {
      await updateUserBlockMutation.mutateAsync({
        instructions: patientInstructions,
        userBlockId,
        userId: patient,
      });

      closeBlockDrawer();
    }
  };

  const openCreateBlockDrawer = (
    blockType: BlockType = BLOCK_TYPE.Nutrition
  ) => {
    resetCreateBlockMutationState();
    setSelectedLaneBlock(null);
    setSelectedCreateBlock({
      block_type: blockType,
      id: null,
      archetype: null,
      name: null,
      stable_string: null,
    });
    setPatientInstructions('');
  };

  const openEditBlockDrawer = (block: UserBlock) => {
    resetCreateBlockMutationState();
    setSelectedLaneBlock(block);
    setSelectedCreateBlock(null);
    setPatientInstructions(block.instructions || '');
  };

  const openNutritionBlockDetailsDrawer = (block: UserBlock) => {
    if (
      !block.stable_string ||
      !ADJUSTABLE_NUTRITION_BLOCKS.includes(block.stable_string) ||
      !block.id
    ) {
      return;
    }

    resetCreateBlockMutationState();
    setSelectedLaneBlock(block);
    setSelectedCreateBlock(null);
    setPatientInstructions('');
  };

  const onBackToBlockList = () => {
    resetCreateBlockMutationState();
    setPatientInstructions('');
    setSelectedCreateBlock((prevSelectedBlock) =>
      prevSelectedBlock
        ? {
            id: null,
            name: null,
            archetype: null,
            stable_string: null,
            block_type: prevSelectedBlock.block_type,
          }
        : null
    );
  };

  const onSelectCreateBlock = (block: DashboardBlock) => {
    resetCreateBlockMutationState();
    setSelectedCreateBlock(block);
  };

  const drawerHeader = isEditMode
    ? selectedLaneBlock?.block_name ?? ''
    : isCreateMode
    ? `Create ${laneLabelByType[selectedCreateBlock?.block_type] ?? ''} Block`
    : '';

  // Once a specific block is picked, prefix its archetype, e.g.
  // "Goal block assignment details".
  const createBlockArchetype = selectedCreateBlock?.archetype;
  const createBlockSubtitle = createBlockArchetype
    ? `${
        createBlockArchetype.charAt(0).toUpperCase() +
        createBlockArchetype.slice(1)
      } block assignment details`
    : 'Block assignment details';

  const drawerSubtitle = isEditMode
    ? selectedLaneBlock?.instructions ||
      `Customize this care block for ${
        user?.first_name ? `${user.first_name}'s` : "the member's"
      } treatment plan.`
    : isCreateMode
    ? createBlockSubtitle
    : '';

  const isSubmittingCreateBlock = isCreateMode && !!selectedCreateBlock;

  const drawerError = () => {
    if (isSubmittingCreateBlock && addUserBlock.isError) {
      return "We couldn't create this block. Please try again later.";
    } else if (!isEditMode && !isSubmittingCreateBlock) {
      return `We couldn't load ${
        laneLabelByType[selectedCreateBlock?.block_type ?? '']?.toLowerCase() ??
        'care'
      } block types. Please try again later.`;
    } else if (isEditMode) {
      if (userBlockAction.isError) {
        return `We couldn't perform the selected action on the ${selectedLaneBlock?.block_name} block`;
      } else if (updateUserBlockMutation.isError) {
        return `We couldn't update the ${selectedLaneBlock?.block_name} block`;
      }
    }
  };

  return {
    drawerHeader,
    drawerSubtitle,
    drawerError: drawerError(),
    isBlockDrawerOpen: isDrawerOpen,
    closeBlockDrawer,
    handleUpdateUserBlock,
    handleUserBlockAction,
    onBackToBlockList,
    onCreateBlock,
    onPatientInstructionsChange: setPatientInstructions,
    onSelectBlock: onSelectCreateBlock,
    openCreateBlockDrawer,
    openEditBlockDrawer,
    openNutritionBlockDetailsDrawer,
    patientInstructions,
    selectedLaneBlock,
    selectedBlock: selectedCreateBlock,
    archivedFilter,
    setArchivedFilter,
  };
};
